import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { logEvent } from "@/lib/analytics";
import { importRealData, addOneAccountFromSample } from "@/lib/sampleData";
import { Pulse, Target, Lightning, ArrowRight, UploadSimple, X, PlusCircle } from "@phosphor-icons/react";

const STEPS = [
  { testid: "health-gauge", icon: Pulse, title: "Recovery Health Score", body: "We scored the whole portfolio on collectability. A low score means a lot is stuck — and highly recoverable.", cta: "Next" },
  { testid: "top-account-row-0", icon: Target, title: "Your #1 account to chase", body: "Accounts are ranked by recoverable money stuck, so your team always works the highest-impact case first.", cta: "Next" },
  { testid: "top-account-menu-0", icon: Lightning, title: "Act in one click", body: "Log a call, schedule a follow-up or start a settlement right here — no spreadsheets, no toggling tools.", cta: "Finish", note: "Import your own portfolio to run this for real." },
];

const PAD = 8;

// Moment 3 — 3-step spotlight tour on the populated dashboard. Uses a box-shadow
// cutout to spotlight each live element and nudges toward importing real data.
export function SampleTour({ onDone }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);

  const measure = useCallback(() => {
    const el = document.querySelector(`[data-testid="${STEPS[i].testid}"]`);
    if (!el) { setRect(null); return false; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    return true;
  }, [i]);

  // Wait for the target of the current step to exist, then measure.
  useEffect(() => {
    setReady(false);
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      const ok = measure();
      if (ok) { setReady(true); clearInterval(iv); logEvent("sample_tour_step", { step: i + 1 }); }
      else if (tries > 40) { clearInterval(iv); onDone(); } // ~8s safety net
    }, 200);
    return () => clearInterval(iv);
  }, [i, measure, onDone]);

  useLayoutEffect(() => {
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [measure]);

  const finish = (imported) => {
    logEvent("sample_tour_completed", { imported: !!imported });
    onDone();
    if (imported) importRealData(navigate, "tour");
  };

  const step = STEPS[i];
  const Icon = step.icon;

  // Tooltip position: below the target if room, else above.
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const below = rect ? rect.top + rect.height + 300 < vh : true;
  const tipTop = rect ? (below ? rect.top + rect.height + PAD + 6 : Math.max(12, rect.top - 12)) : 0;
  const tipTransform = rect && !below ? "translateY(-100%)" : "none";
  const tipLeft = rect ? Math.max(12, Math.min(rect.left, (window.innerWidth || 1200) - 360)) : 0;

  if (!ready || !rect) return <div className="fixed inset-0 z-[115] bg-black/50" data-testid="sample-tour-scrim" />;

  return (
    <div className="fixed inset-0 z-[115]" data-testid="sample-tour" aria-live="polite">
      {/* spotlight cutout */}
      <motion.div
        className="absolute rounded-sm pointer-events-none"
        animate={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
        transition={{ type: "spring", stiffness: 240, damping: 28 }}
        style={{ boxShadow: "0 0 0 9999px rgba(2,6,23,0.62)", outline: "2px solid #16A34A", outlineOffset: 0 }}
      />
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className="absolute w-[340px] max-w-[calc(100vw-24px)] bg-white rounded-sm shadow-2xl border border-gray-200 border-l-4 border-l-[#0B3D91] p-5 text-left"
          style={{ top: tipTop, left: tipLeft, transform: tipTransform }}
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Step {i + 1} of {STEPS.length}</span>
            <button onClick={() => finish(false)} data-testid="sample-tour-close" aria-label="Close tour" className="text-gray-400 hover:text-gray-700"><X size={16} weight="bold" /></button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="h-8 w-8 rounded-sm bg-[#0B3D91]/10 flex items-center justify-center shrink-0"><Icon size={17} weight="bold" className="text-[#0B3D91]" /></div>
            <h3 className="font-black text-lg text-[#0B3D91] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{step.title}</h3>
          </div>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.body}</p>
          {step.note && (
            <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#16A34A]">
              <UploadSimple size={13} weight="bold" /> {step.note}
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              {STEPS.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-4 bg-[#0B3D91]" : "w-1.5 bg-gray-300"}`} />)}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {i === STEPS.length - 1 ? (
                <>
                  <button onClick={() => finish(false)} data-testid="sample-tour-explore" className="text-sm font-semibold text-gray-500 hover:text-gray-900">Explore on my own</button>
                  <button onClick={() => { logEvent("sample_tour_completed", { imported: false, add_one: true }); onDone(); addOneAccountFromSample("sample_tour"); }} data-testid="sample-tour-add-one" className="inline-flex items-center gap-1.5 border-2 border-[#0B3D91] text-[#0B3D91] rounded-sm px-3 py-2 text-sm font-bold hover:bg-[#0B3D91]/[0.06] transition-colors">
                    <PlusCircle size={14} weight="bold" /> Add one account
                  </button>
                  <button onClick={() => finish(true)} data-testid="sample-tour-import" className="inline-flex items-center gap-1.5 bg-[#16A34A] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-[#138a3e] transition-colors">
                    <UploadSimple size={14} weight="bold" /> Import my data
                  </button>
                </>
              ) : (
                <button onClick={() => setI((v) => v + 1)} data-testid="sample-tour-next" className="inline-flex items-center gap-1.5 bg-[#0B3D91] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-[#0a347d] transition-colors">
                  {step.cta} <ArrowRight size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
